import { useState, type ReactNode } from 'react';
import { createRoute, Link, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Label } from '../components/ui/Label.js';
import { ApiError, logoutRequest } from '../lib/api.js';
import { acceptInvitation, fetchInvitation } from '../lib/invitations.js';
import { useAuthStore } from '../lib/auth.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup/$token',
  component: SignupPage,
});

/** Brand chrome shared by every state of the signup page. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 justify-center">
          <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
            <span className="font-serif text-cream text-lg leading-none">A</span>
          </div>
          <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
        </div>
        <div className="bg-white border border-line rounded-2xl shadow-card p-8">{children}</div>
      </div>
    </div>
  );
}

function StateMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="text-center">
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">{title}</h1>
      <p className="text-sm text-muted mb-6">{body}</p>
      <Link to="/login">
        <Button variant="secondary" className="w-full">
          Go to login
        </Button>
      </Link>
    </div>
  );
}

function SignupPage() {
  const { token } = Route.useParams();
  const router = useRouter();
  const setSession = useAuthStore((s) => s.setSession);
  const currentUser = useAuthStore((s) => s.user);

  const [loggedOut, setLoggedOut] = useState(false);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => fetchInvitation(token),
    retry: false,
    staleTime: Infinity,
  });

  // ---- Loading ----
  if (isLoading) {
    return (
      <Shell>
        <div className="flex flex-col items-center py-6">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-navy border-r-transparent" />
          <p className="text-sm text-muted mt-4">Checking your invite…</p>
        </div>
      </Shell>
    );
  }

  // ---- Error states ----
  if (error || !data) {
    const status = error instanceof ApiError ? error.status : 0;
    if (status === 410) {
      return (
        <Shell>
          <StateMessage
            title="This invite has expired"
            body="Ask the person who invited you to send a fresh one."
          />
        </Shell>
      );
    }
    if (status === 409) {
      return (
        <Shell>
          <StateMessage
            title="This invite has already been used"
            body="If this account is yours, try logging in instead."
          />
        </Shell>
      );
    }
    return (
      <Shell>
        <StateMessage
          title="This invite link isn't valid"
          body="Ask the person who invited you to send a fresh one."
        />
      </Shell>
    );
  }

  // ---- Logged-in gate ----
  if (currentUser && !loggedOut) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">
            You&rsquo;re already logged in
          </h1>
          <div
            className="rounded-lg px-4 py-3 text-sm text-left mb-6"
            style={{ backgroundColor: '#FBF3E0', color: '#8A6D1F', border: '1px solid #EAD9A8' }}
          >
            You&rsquo;re currently logged in as{' '}
            <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>
              {currentUser.email}
            </span>
            . Log out to accept this invite for{' '}
            <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>
              {data.email}
            </span>
            .
          </div>
          <div className="flex flex-col gap-2">
            <Button
              className="w-full"
              onClick={async () => {
                await logoutRequest();
                setLoggedOut(true);
              }}
            >
              Log out and accept this invite
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => router.navigate({ to: '/app' })}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ---- Signup form ----
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await acceptInvitation(token, fullName.trim(), password);
      setSession(result.accessToken, result.user);
      router.navigate({ to: '/app' });
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-1">
        Welcome to {data.tenant_name}
      </h1>
      <p className="text-sm text-muted mb-6">
        {data.inviter_name} invited you as a {data.role_label}.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="signup-email">Email</Label>
          <Input id="signup-email" type="email" value={data.email} disabled />
        </div>

        <div>
          <Label htmlFor="signup-name">Full name</Label>
          <Input
            id="signup-name"
            autoComplete="name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name"
          />
        </div>

        <div>
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
          />
          <p className="text-xs text-muted mt-1.5">At least 10 characters.</p>
        </div>

        {formError && (
          <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
            {formError}
          </div>
        )}

        <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
          Create my account
        </Button>
      </form>
    </Shell>
  );
}
